import { useNavigate } from "react-router-dom";
import { Check, Star, Sparkles, Wrench, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PLAN_LIST, TRIAL_DAYS, DONE_FOR_YOU_FEE, OVERAGE_NOTE } from "@/components/pricingData";
import { useAuth } from "@/context/AuthContext";

export function PricingCards({ onSelect, currentPlan }) {
  return (
    <div className="grid lg:grid-cols-3 gap-6 items-start">
      {PLAN_LIST.map((p) => {
        const popular = p.popular;
        const isCurrent = currentPlan === p.id;
        return (
          <div
            key={p.id}
            data-testid={`plan-card-${p.id}`}
            className={`relative rounded-2xl bg-white p-7 transition-all duration-200 flex flex-col ${
              popular ? "border-2 border-blue-600 shadow-xl lg:-mt-4 lg:mb-4" : "border border-slate-200 hover:border-slate-300 hover:shadow-lg"
            }`}
          >
            {popular && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-blue-600 text-white text-xs font-bold px-3 py-1 shadow">
                <Star className="h-3.5 w-3.5 fill-white" /> Most Popular
              </span>
            )}
            <div>
              <h3 className="font-heading text-xl font-extrabold tracking-tight">{p.name}</h3>
              <p className="text-sm text-slate-500 mt-1">{p.tagline}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-heading text-4xl font-extrabold tracking-tight">£{p.price}</span>
                <span className="text-slate-500 text-sm">/month</span>
              </div>
              {p.inheritsFrom && <p className="mt-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Everything in {p.inheritsFrom}, plus:</p>}
            </div>
            <ul className="mt-5 space-y-2.5 flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Check className={`h-4 w-4 mt-0.5 shrink-0 ${popular ? "text-blue-600" : "text-emerald-500"}`} />
                  {f}
                </li>
              ))}
            </ul>
            <Button
              data-testid={`select-plan-${p.id}`}
              onClick={() => onSelect(p.id)}
              className={`mt-6 w-full gap-2 ${popular ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"}`}
            >
              {isCurrent ? "Current plan" : <>Start free trial <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </div>
        );
      })}
    </div>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const onSelect = (planId) => {
    if (user && user.id) navigate(`/onboarding?plan=${planId}`);
    else navigate(`/register?plan=${planId}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-1.5">
            <img src="/operamind-mark.png" alt="OperaMind" className="h-10 w-10 object-contain" />
            <div className="flex flex-col leading-none text-left">
              <span className="font-heading font-extrabold text-lg tracking-tight text-slate-900">Opera<span className="text-blue-600">Mind</span></span>
              <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-900">Intelligence. Orchestrated.</span>
            </div>
          </button>
          <Button variant="outline" className="border-slate-300" onClick={() => navigate(user && user.id ? "/dashboard" : "/login")}>
            {user && user.id ? "Dashboard" : "Sign in"}
          </Button>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-14 pb-8 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
          <Sparkles className="h-3.5 w-3.5 text-blue-600" /> Pricing
        </span>
        <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter">Simple pricing that pays for itself.</h1>
        <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
          Every plan starts with a <span className="font-semibold text-slate-900">{TRIAL_DAYS}-day free trial</span>.
          Recover a few missed calls a month and the service pays for itself.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-10">
        <PricingCards onSelect={onSelect} currentPlan={null} />
      </section>

      {/* Setup options */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-7">
            <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600"><Sparkles className="h-5 w-5" /></div>
            <h3 className="mt-4 font-heading font-bold text-lg tracking-tight">Free self-service setup</h3>
            <p className="mt-2 text-sm text-slate-600">Configure everything yourself with the guided OperaMind onboarding wizard. Live in minutes.</p>
            <p className="mt-4 font-heading text-2xl font-extrabold">Free</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-7">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600"><Wrench className="h-5 w-5" /></div>
            <h3 className="mt-4 font-heading font-bold text-lg tracking-tight">Done-for-you setup</h3>
            <p className="mt-2 text-sm text-slate-600">We configure your receptionist, greetings, FAQs, services, hours, languages and integrations for you.</p>
            <p className="mt-4 font-heading text-2xl font-extrabold">£{DONE_FOR_YOU_FEE} <span className="text-sm font-medium text-slate-500">one-time</span></p>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-slate-400">{OVERAGE_NOTE}</p>
      </section>
    </div>
  );
}
